import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import ProductGrid from "@/components/catalog/ProductGrid";
import ProductGridSkeleton from "@/components/catalog/ProductGridSkeleton";
import ProductFilters from "@/components/catalog/ProductFilters";
import ProductSearch from "@/components/catalog/ProductSearch";
import DiscountPopup from "@/components/catalog/DiscountPopup";
import PageSizeSelector from "@/components/catalog/PageSizeSelector";
import CatalogPagination from "@/components/catalog/CatalogPagination";
import SEOHead from "@/components/SEOHead";
import { Skeleton } from "@/components/ui/skeleton";
import { useProducts } from "@/hooks/useProducts";
import type { PetType } from "@/types/database";

const Catalog = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(
    searchParams.get("category") || "all"
  );
  const [selectedPetType, setSelectedPetType] = useState<PetType | 'all'>(
    (searchParams.get("petType") as PetType) || "all"
  );
  const [showDiscountPopup, setShowDiscountPopup] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(24);

  useEffect(() => {
    const alreadyClaimed = localStorage.getItem("gordon_discount_claimed") === "true";
    if (alreadyClaimed) return;

    const dismissedAt = localStorage.getItem("gordon_discount_dismissed_at");
    if (dismissedAt) {
      const dismissedTime = parseInt(dismissedAt, 10);
      const twentyFourHours = 24 * 60 * 60 * 1000;
      const now = Date.now();
      
      if (now - dismissedTime < twentyFourHours) {
        return;
      }
      localStorage.removeItem("gordon_discount_dismissed_at");
    }

    const timer = setTimeout(() => {
      setShowDiscountPopup(true);
    }, 10000);
    
    return () => clearTimeout(timer);
  }, []);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, selectedPetType, searchQuery]);

  const { data, isLoading, error } = useProducts({
    category: selectedCategory,
    petType: selectedPetType,
    search: searchQuery,
    page: currentPage,
    pageSize: pageSize,
  });

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    updateSearchParams(selectedPetType, category);
  };

  const handlePetTypeChange = (petType: string) => {
    setSelectedPetType(petType as PetType | 'all');
    updateSearchParams(petType, selectedCategory);
  };

  const updateSearchParams = (petType: string, category: string) => {
    const params: Record<string, string> = {};
    if (petType !== "all") params.petType = petType;
    if (category !== "all") params.category = category;
    setSearchParams(params);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const getPageTitle = () => {
    if (selectedPetType === "dog") return "Productos para Perros";
    if (selectedPetType === "cat") return "Productos para Gatos";
    if (selectedPetType === "other") return "Productos para Otras Mascotas";
    return "Nuestro Catálogo";
  };

  const getPageDescription = () => {
    if (selectedPetType === "dog") return "Todo lo que tu perro necesita para estar feliz y saludable";
    if (selectedPetType === "cat") return "Mimá a tu gato con los mejores productos del mercado";
    if (selectedPetType === "other") return "Productos especializados para aves, peces y otras mascotas";
    return "Explorá nuestra amplia variedad de productos premium para el cuidado de tu mascota";
  };

  const getSeoKeywords = () => {
    if (selectedPetType === "dog") return "productos perros, alimento perros, accesorios perros, Costa Rica";
    if (selectedPetType === "cat") return "productos gatos, alimento gatos, accesorios gatos, Costa Rica";
    if (selectedPetType === "other") return "productos mascotas, alimento aves, accesorios mascotas, Costa Rica";
    return "productos mascotas, alimento perros, alimento gatos, accesorios mascotas, Costa Rica";
  };

  const products = data?.products || [];
  const totalCount = data?.totalCount || 0;
  const totalPages = data?.totalPages || 1;

  // Calculate display range
  const fromItem = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const toItem = Math.min(currentPage * pageSize, totalCount);

  // Build breadcrumbs for SEO
  const breadcrumbs = [
    { name: "Inicio", url: "/" },
    { name: "Catálogo", url: "/catalogo" },
    ...(selectedPetType === "dog" ? [{ name: "Perros", url: "/catalogo?petType=dog" }] : []),
    ...(selectedPetType === "cat" ? [{ name: "Gatos", url: "/catalogo?petType=cat" }] : []),
    ...(selectedPetType === "other" ? [{ name: "Otros", url: "/catalogo?petType=other" }] : []),
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={getPageTitle()}
        description={getPageDescription()}
        keywords={getSeoKeywords()}
        breadcrumbs={breadcrumbs}
      />
      <Header />

      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Page Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">{getPageTitle()}</h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            {getPageDescription()}
          </p>
        </div>

        {/* Search */}
        <ProductSearch value={searchQuery} onChange={setSearchQuery} />

        {/* Filters */}
        <ProductFilters
          selectedCategory={selectedCategory}
          selectedPetType={selectedPetType}
          onCategoryChange={handleCategoryChange}
          onPetTypeChange={handlePetTypeChange}
        />

        {/* Results count and page size selector */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          {isLoading ? (
            <Skeleton className="h-5 w-48" />
          ) : error ? (
            <p className="text-destructive">Error al cargar productos</p>
          ) : (
            <p className="text-muted-foreground">
              Mostrando {fromItem}-{toItem} de {totalCount} productos
            </p>
          )}
          <PageSizeSelector value={pageSize} onChange={handlePageSizeChange} />
        </div>

        {/* Products Grid */}
        {isLoading ? (
          <ProductGridSkeleton count={pageSize} />
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-destructive">Error al cargar los productos. Por favor intenta de nuevo.</p>
          </div>
        ) : (
          <>
            <ProductGrid products={products} />
            
            {/* Pagination */}
            {totalPages > 1 && (
              <CatalogPagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            )}
          </>
        )}
      </main>

      <Footer />

      {showDiscountPopup && (
        <DiscountPopup onClose={() => setShowDiscountPopup(false)} />
      )}
    </div>
  );
};

export default Catalog;
